/**
 * TaxonomyTag - Taxonomy classification pill for conduct labels
 *
 * Atomic level: Molecule
 * Used in: ConductLabels section to display taxonomy labels
 *
 * Color variants based on the conduct dimension:
 * - dissatisfaction: teal (design token)
 * - strain: purple (design token)
 * - harm: berry (design token)
 * - vulnerability: muted-foreground (design token)
 *
 * Supports optional tooltip with reasoning, confidence, and regulatory reference
 */

import React, { useState, Fragment } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../../lib/utils';
import { formatConductLabel } from './taxonomyLabelFormatting';

const taxonomyTagVariants = cva(
  'inline-flex items-center justify-center px-3 py-1.5 rounded-md text-xs font-medium border bg-white cursor-default whitespace-nowrap',
  {
    variants: {
      variant: {
        dissatisfaction: 'border-teal text-teal',
        strain: 'border-purple text-purple',
        harm: 'border-berry text-berry',
        vulnerability: 'border-purple text-purple',
        'root-cause': 'border-pink text-pink',
        'consumer-duty': 'border-blue-600 text-blue-600',
        tcf: 'border-green-700 text-green-700',
        neutral: 'border-gray-300 text-gray-600',
      },
    },
    defaultVariants: {
      variant: 'neutral',
    },
  }
);

/**
 * Tooltip data for taxonomy labels
 */
export interface TaxonomyTooltipData {
  label_name?: string;
  rationale?: string;
  regulation_reference?: string;
  confidence?: number; // 0.0 - 1.0 or 0-100
}

interface TaxonomyTagProps extends VariantProps<typeof taxonomyTagVariants> {
  label: string;
  displayLabel?: string;
  tooltip?: TaxonomyTooltipData;
  className?: string;
}

const TaxonomyTag: React.FC<TaxonomyTagProps> = ({ className, variant, label, displayLabel, tooltip }) => {
    const [isOpen, setIsOpen] = useState(false);
    const formattedLabel = displayLabel || formatConductLabel(label, variant);

    // Normalize confidence to percentage
    const confidencePct = tooltip?.confidence !== undefined
      ? (tooltip.confidence > 1 ? tooltip.confidence : tooltip.confidence * 100)
      : undefined;

    // If no tooltip data, render simple span
    if (!tooltip) {
      return (
        <span className={cn(taxonomyTagVariants({ variant }), className)}>
          {formattedLabel}
        </span>
      );
    }

    // Render with Popover tooltip
    return (
      <Popover
        className="relative inline-block"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <Popover.Button
          as="span"
          className={cn(taxonomyTagVariants({ variant }), 'cursor-help', className)}
          aria-label={`${formattedLabel} details`}
        >
          {formattedLabel}
        </Popover.Button>

        <Transition
          as={Fragment}
          show={isOpen}
          enter="transition ease-out duration-150"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <Popover.Panel
            static
            className="absolute z-50 mt-2 w-72 left-0"
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
          >
            <div className="rounded-lg border border-border bg-card shadow-lg p-3 text-figma-sm">
              <div className="font-semibold text-foreground mb-1">
                {tooltip.label_name || formattedLabel}
              </div>

              {tooltip.rationale && (
                <p className="text-muted-foreground mb-2 whitespace-pre-wrap text-figma-xs leading-relaxed">
                  {tooltip.rationale}
                </p>
              )}

              <div className="space-y-1 text-figma-xs">
                {tooltip.regulation_reference && (
                  <div className="text-muted-foreground">
                    <span className="font-medium text-foreground">Regulation:</span>{' '}
                    {tooltip.regulation_reference}
                  </div>
                )}
                {confidencePct !== undefined && (
                  <div className="text-muted-foreground">
                    <span className="font-medium text-foreground">Confidence:</span>{' '}
                    {Math.round(confidencePct)}%
                  </div>
                )}
              </div>
            </div>
          </Popover.Panel>
        </Transition>
      </Popover>
    );
};

export type TaxonomyTagVariant = NonNullable<VariantProps<typeof taxonomyTagVariants>['variant']>;

export { TaxonomyTag, taxonomyTagVariants };
